import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileDown, ShieldCheck } from "lucide-react";

const fmt = (v) => Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PERIODS = ["Q1", "Q2", "Q3", "Q4", "H1", "H2", "FULL"];

const VatGroupConfig = ({ companyId, cfg, onSaved }) => {
  const [form, setForm] = useState({ enabled: false, group_uid: "", member_ids: [] });
  useEffect(() => setForm({ enabled: !!cfg?.enabled, group_uid: cfg?.group_uid || "", member_ids: (cfg?.members || []).map((m) => m.id) }), [cfg]);
  const toggle = (id) => setForm((f) => ({ ...f, member_ids: f.member_ids.includes(id) ? f.member_ids.filter((x) => x !== id) : [...f.member_ids, id] }));
  const save = async () => {
    try {
      await apiClient.put(`/companies/${companyId}/holding/vat-group`, form);
      toast.success(form.enabled ? "MWST-Gruppe gespeichert" : "MWST-Gruppe deaktiviert");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="vat-group-config">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-medium text-slate-800">Gruppenträger: {cfg?.leader}</div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500">Gruppenbesteuerung aktiv</Label>
          <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="vat-group-enabled-switch" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-500">Gruppen-UID (ESTV)</Label>
          <Input placeholder="CHE-123.456.789 MWST" value={form.group_uid} onChange={(e) => setForm({ ...form, group_uid: e.target.value })} data-testid="vat-group-uid-input" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Mitglieder</Label>
          <div className="space-y-1 mt-1">
            {(cfg?.candidates || []).map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm" data-testid={`vat-group-member-${c.id}`}>
                <Checkbox checked={form.member_ids.includes(c.id) || c.id === cfg.leader_id} disabled={c.id === cfg.leader_id} onCheckedChange={() => toggle(c.id)} data-testid={`vat-group-member-cb-${c.id}`} />
                <span>{c.name}</span>
                {c.vat_method === "saldo" && <Badge variant="outline" className="text-[10px] border-rose-300 text-rose-700">Saldosteuersatz – unzulässig</Badge>}
              </label>
            ))}
          </div>
        </div>
      </div>
      <Button size="sm" onClick={save} data-testid="vat-group-save-btn">Speichern</Button>
      <p className="text-[11px] text-slate-400">Art. 13 MWSTG: Eine Gruppe reicht eine gemeinsame Abrechnung unter der Gruppen-UID ein; Leistungen zwischen Mitgliedern (Innenumsätze) sind nicht steuerbar.</p>
    </div>
  );
};

export const VatGroupCard = ({ companyId, year }) => {
  const [cfg, setCfg] = useState(null);
  const [period, setPeriod] = useState("FULL");
  const [stmt, setStmt] = useState(null);
  const [loading, setLoading] = useState(false);
  const loadCfg = useCallback(() => apiClient.get(`/companies/${companyId}/holding/vat-group`).then((r) => setCfg(r.data)).catch(() => {}), [companyId]);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const load = async () => {
    setLoading(true);
    try { setStmt((await apiClient.get(`/companies/${companyId}/holding/vat-group/statement`, { params: { year, period } })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Gruppenabrechnung fehlgeschlagen"); }
    finally { setLoading(false); }
  };
  const download = async () => {
    try {
      const r = await apiClient.get(`/companies/${companyId}/holding/vat-group/emwst-xml`, { params: { year, period }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/xml" }));
      const a = document.createElement("a"); a.href = url; a.download = `emwst_gruppe_${year}_${period}.xml`; a.click(); URL.revokeObjectURL(url);
      toast.success(r.headers["x-schema-valid"] === "1" ? "Gruppen-XML erzeugt (eCH-0217 gültig)" : "XML erzeugt – Schema-Warnungen prüfen");
    } catch (e) { toast.error(e.response?.data?.detail || "Download fehlgeschlagen"); }
  };

  return (
    <Card className="p-4 space-y-4" data-testid="holding-vat-group-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> MWST-Gruppenbesteuerung</h2>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-28 h-8" data-testid="vat-group-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map((p) => <SelectItem key={p} value={p}>{p === "FULL" ? "Jahr" : p}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading || !cfg?.enabled} data-testid="vat-group-load-btn">{loading ? "Lade…" : "Gruppenabrechnung"}</Button>
          <Button size="sm" onClick={download} disabled={!stmt} data-testid="vat-group-xml-btn"><FileDown className="h-4 w-4 mr-1" /> eMWST-XML</Button>
        </div>
      </div>
      {cfg && <VatGroupConfig companyId={companyId} cfg={cfg} onSaved={loadCfg} />}
      {stmt && (
        <div className="space-y-3" data-testid="vat-group-statement">
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="outline">{stmt.period_label} {stmt.year}</Badge>
            <Badge variant="outline">UID {stmt.group_uid || "— fehlt —"}</Badge>
            {stmt.members.map((m) => <Badge key={m.id} variant="outline" className="border-violet-300 text-violet-700">{m.name}</Badge>)}
          </div>
          {stmt.warnings?.length > 0 && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 space-y-1" data-testid="vat-group-warnings">
              {stmt.warnings.map((w, i) => <div key={i}>⚠️ {w}</div>)}
            </div>
          )}
          <div className="rounded-lg border border-slate-200 p-2.5 text-sm" data-testid="vat-group-inner">
            <span className="text-slate-500">Eliminierte Innenumsätze:</span> <span className="font-mono font-semibold">CHF {fmt(stmt.inner_turnover.net)}</span>
            {stmt.inner_turnover.tax > 0 && <span className="text-amber-700"> (davon fälschlich fakturierte MWST CHF {fmt(stmt.inner_turnover.tax)})</span>}
            {stmt.inner_turnover.pairs.length > 0 && <div className="text-xs text-slate-500 mt-1">{stmt.inner_turnover.pairs.map((p, i) => <div key={i}>{p.from} → {p.to}: {fmt(p.net)}</div>)}</div>}
          </div>
          <div className="overflow-x-auto border rounded-lg">
            <Table data-testid="vat-group-figures-table">
              <TableHeader><TableRow><TableHead className="w-16">Ziffer</TableHead><TableHead>Bezeichnung</TableHead>
                {stmt.members.map((m) => <TableHead key={m.id} className="text-right">{m.name}</TableHead>)}<TableHead className="text-right">Gruppe</TableHead></TableRow></TableHeader>
              <TableBody>
                {stmt.rows.filter((r) => r.value || ["200", "299", "399", "479", "500", "510"].includes(r.figure)).map((r) => (
                  <TableRow key={r.figure} className={["299", "399", "479", "500", "510"].includes(r.figure) ? "bg-slate-50 font-semibold" : ""} data-testid={`vat-group-figure-${r.figure}`}>
                    <TableCell className="font-mono">{r.figure}</TableCell><TableCell className="text-sm">{r.label}</TableCell>
                    {stmt.members.map((m) => <TableCell key={m.id} className="text-right font-mono text-sm text-slate-500">{fmt(m.figures?.figures?.[r.figure])}</TableCell>)}
                    <TableCell className="text-right font-mono text-sm">{fmt(r.value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </Card>
  );
};
